import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { SlidersHorizontal, AlertTriangle, CheckCircle, AlertCircle, Info, ChevronRight, X, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../store/authStore';
import { getAlerts, resolveAlert } from '../../api/alerts';
import { SkeletonBlock } from '../../components/SkeletonBlock';
import { Button } from '../../components/Button';
interface Alert {
  id: string;
  sessionId: string;
  type: string;
  message: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  status: 'active' | 'resolved';
  triggeredAt: string;
  resolvedAt: string | null;
}
export function AlertsList() {
  const navigate = useNavigate();
  const {
    token
  } = useAuth();
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  // Filter state
  const [showFilters, setShowFilters] = useState(false);
  const [statusFilter, setStatusFilter] = useState<'active' | 'resolved'>('active');
  const [severityFilters, setSeverityFilters] = useState<string[]>(['all']);
  // Resolve All Modal state
  const [showResolveAllModal, setShowResolveAllModal] = useState(false);
  const [isResolvingAll, setIsResolvingAll] = useState(false);
  const fetchAlerts = async () => {
    if (!token) return;
    setIsLoading(true);
    setError(false);
    try {
      const res = await getAlerts(token);
      setAlerts(res || []);
    } catch (err) {
      // console.error('Failed to fetch alerts', err)
      setError(true);
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    fetchAlerts();
  }, [token]);
  const activeAlertsCount = alerts.filter(a => a.status === 'active').length;
  const handleResolveAll = async () => {
    if (!token) return;
    setIsResolvingAll(true);
    try {
      const activeAlerts = alerts.filter(a => a.status === 'active');
      for (const alert of activeAlerts) {
        await resolveAlert(token, alert.id);
      }
      await fetchAlerts();
      setShowResolveAllModal(false);
    } catch (err) {
      // console.error('Failed to resolve all alerts', err)
    } finally {
      setIsResolvingAll(false);
    }
  };
  const handleResolveSingle = async (id: string) => {
    if (!token) return;
    try {
      await resolveAlert(token, id);
      setAlerts(prev => prev.map(a => a.id === id ? {
        ...a,
        status: 'resolved'
      } : a));
    } catch (err) {
      // console.error('Failed to resolve alert', err)
    }
  };
  const toggleSeverityFilter = (sev: string) => {
    if (sev === 'all') {
      setSeverityFilters(['all']);
      return;
    }
    let newFilters = severityFilters.filter(f => f !== 'all');
    if (newFilters.includes(sev)) {
      newFilters = newFilters.filter(f => f !== sev);
      if (newFilters.length === 0) newFilters = ['all'];
    } else {
      newFilters.push(sev);
    }
    setSeverityFilters(newFilters);
  };
  const filteredAlerts = alerts.filter(alert => {
    if (alert.status !== statusFilter) return false;
    if (severityFilters.includes('all')) return true;
    if (alert.severity === 'critical' && severityFilters.includes('high')) return true;
    return severityFilters.includes(alert.severity);
  });
  const activeFiltered = filteredAlerts.filter(a => a.status === 'active');
  const resolvedFiltered = filteredAlerts.filter(a => a.status === 'resolved');
  return <main className="w-full max-w-[800px] mx-auto pb-20 md:pb-0" data-id="element-409" data-mp="h9zzkf7piaeo">
      {/* Header */}
      <div className="flex items-center justify-between mb-6" data-id="element-410" data-mp="cb6osujt0xi7">
        <div data-id="element-411" data-mp="c9vjgqapnhoz">
          <h1 className="text-[24px] font-semibold text-text-primary" data-id="element-412" data-mp="cd9knv7n1kuu">
            Alerts
          </h1>
          <p className="text-[14px] text-text-secondary mt-1" data-id="element-413" data-mp="1sr9mjbq1nkkq">
            {isLoading ? 'Loading...' : activeAlertsCount === 0 ? 'All clear' : `${activeAlertsCount} active alert${activeAlertsCount === 1 ? '' : 's'}`}
          </p>
        </div>
        <button onClick={() => setShowFilters(true)} className="w-[40px] h-[40px] rounded-full bg-[#E9D5FF] flex items-center justify-center hover:bg-[#E5E2DD] transition-colors" aria-label="Filter" data-id="element-414" data-mp="2a5xb2ilrhyt">
          <SlidersHorizontal size={22} className="text-text-primary" data-id="element-415" data-mp="g71an75vzvko" />
        </button>
      </div>

      {/* Summary Banner */}
      {!isLoading && !error && <div className="mb-8" data-id="element-416" data-mp="pxvw49bb6r52">
          {activeAlertsCount > 0 ? <div className="bg-danger/5 border border-danger/20 rounded-[16px] p-4 flex items-center justify-between" data-id="element-417" data-mp="pyp5zj5kdwrf">
              <div className="flex items-center gap-3" data-id="element-418" data-mp="u1brz3fk0ngp">
                <AlertTriangle size={24} className="text-danger flex-shrink-0" data-id="element-419" data-mp="yzwrt1qw954m" />
                <div data-id="element-420" data-mp="13vkffcpq9q80">
                  <h3 className="text-[15px] font-semibold text-danger" data-id="element-421" data-mp="1e1c26ery8v75">
                    Active Alerts Require Attention
                  </h3>
                  <p className="text-[13px] text-text-secondary" data-id="element-422" data-mp="190xrsq2vl95y">
                    {activeAlertsCount} alert
                    {activeAlertsCount === 1 ? '' : 's'} need to be reviewed
                  </p>
                </div>
              </div>
              <button onClick={() => setShowResolveAllModal(true)} className="border border-danger text-danger rounded-pill px-[14px] py-[6px] text-[13px] font-medium hover:bg-danger/5 transition-colors whitespace-nowrap ml-4" data-id="element-423" data-mp="1wrtpg3u0iyl4">
                Resolve All
              </button>
            </div> : <div className="bg-success/10 border border-success/30 rounded-[16px] p-4 flex items-center gap-3" data-id="element-424" data-mp="1j0gal371uowf">
              <CheckCircle size={24} className="text-success flex-shrink-0" data-id="element-425" data-mp="2fkp6zzixma5l" />
              <div data-id="element-426" data-mp="2aqbd61jx4b31">
                <h3 className="text-[15px] font-semibold text-success" data-id="element-427" data-mp="2apruujqcvkoh">
                  All Clear
                </h3>
                <p className="text-[13px] text-text-secondary" data-id="element-428" data-mp="dt5ltekomm4n">
                  No active alerts at this time.
                </p>
              </div>
            </div>}
        </div>}

      {/* Content */}
      {isLoading ? <div className="space-y-4" data-id="element-429" data-mp="42av9y0el0de">
          {[1, 2, 3, 4].map(i => <SkeletonBlock key={i} height="120px" borderRadius="20px" data-id="element-430" data-mp="3m8z0dnvgdnj2" />)}
        </div> : error ? <div className="bg-danger/10 border border-danger/20 rounded-[16px] p-6 text-center" data-id="element-431" data-mp="3r939gsu17fz9">
          <AlertTriangle size={32} className="text-danger mx-auto mb-3" data-id="element-432" data-mp="3cc429633dp5b" />
          <h3 className="text-[16px] font-semibold text-text-primary mb-2" data-id="element-433" data-mp="3h9m5qgycbc5x">
            Failed to load alerts
          </h3>
          <p className="text-[14px] text-text-secondary mb-4" data-id="element-434" data-mp="1akzfanc3tcrb">
            There was a problem connecting to the server.
          </p>
          <Button onClick={fetchAlerts} className="mx-auto" data-id="element-435" data-mp="1fgog0tzv0zp7">
            Try Again
          </Button>
        </div> : filteredAlerts.length === 0 ? <div className="py-16 flex flex-col items-center justify-center text-center" data-id="element-436" data-mp="2v2rqwn5hl25s">
          {alerts.length === 0 ? <>
              <div className="w-[120px] h-[120px] mb-6 relative" data-id="element-437" data-mp="39spmvp9nqw5j">
                {/* Simple Shield SVG */}
                <svg viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-438" data-mp="34y37zc4dalgp">
                  <path d="M60 10L15 30V55C15 80 34 103 60 110C86 103 105 80 105 55V30L60 10Z" fill="#F3E8FF" stroke="#00D4D4" strokeWidth="4" strokeLinejoin="round" data-id="element-439" data-mp="3ep69gf17l2gi" />
                  <path d="M60 45C60 45 50 35 40 45C30 55 60 80 60 80C60 80 90 55 80 45C70 35 60 45 60 45Z" fill="#6B21E8" data-id="element-440" data-mp="1ycisd54ajwhi" />
                </svg>
              </div>
              <h3 className="text-[20px] font-semibold text-text-primary mb-2" data-id="element-441" data-mp="17ixhr2gd32io">
                No alerts
              </h3>
              <p className="text-[15px] text-text-secondary max-w-[280px]" data-id="element-442" data-mp="1cfxpriwrufcf">
                Everything looks good. You'll be notified here if anything needs
                attention.
              </p>
            </> : <>
              <h3 className="text-[16px] font-medium text-text-primary mb-2" data-id="element-443" data-mp="olei2pfsmw8x">
                No alerts match your filters
              </h3>
              <button onClick={() => {
          setStatusFilter('active');
          setSeverityFilters(['all']);
        }} className="text-primary font-medium hover:underline" data-id="element-444" data-mp="tl99cu3hdnui">
                Clear filters
              </button>
            </>}
        </div> : <div className="space-y-8" data-id="element-445" data-mp="2evpuaqe8bbqm">
          {activeFiltered.length > 0 && <div data-id="element-446" data-mp="39xcp6ntvam8">
              <div className="flex items-center gap-4 mb-4" data-id="element-447" data-mp="38dyrmpn9bnm">
                <span className="text-[13px] font-semibold text-text-secondary uppercase tracking-wider" data-id="element-448" data-mp="3ufqjgbpqk78a">
                  Active
                </span>
                <div className="flex-1 h-[1px] bg-border" data-id="element-449" data-mp="3pnffmxrvk1ag" />
              </div>
              <div className="space-y-4" data-id="element-450" data-mp="3kovugzl3utj5">
                <AnimatePresence data-id="element-451" data-mp="3knt36qjvep2p">
                  {activeFiltered.map(alert => <AlertCard key={alert.id} alert={alert} onResolve={() => handleResolveSingle(alert.id)} data-id="element-452" data-mp="3fs5f6eh4k1ys" />)}
                </AnimatePresence>
              </div>
            </div>}

          {resolvedFiltered.length > 0 && <div data-id="element-453" data-mp="14zay2i4vecjb">
              <div className="flex items-center gap-4 mb-4" data-id="element-454" data-mp="14xq34ren8rkx">
                <span className="text-[13px] font-semibold text-text-secondary uppercase tracking-wider" data-id="element-455" data-mp="zzokg7l4qu9z">
                  Resolved
                </span>
                <div className="flex-1 h-[1px] bg-border" data-id="element-456" data-mp="2274awrovw9h5" />
              </div>
              <div className="space-y-4" data-id="element-457" data-mp="1x8j9ioxwbjr4">
                {resolvedFiltered.map(alert => <AlertCard key={alert.id} alert={alert} data-id="element-458" data-mp="2b9nddaorck2v" />)}
              </div>
            </div>}
        </div>}

      {/* Filter Bottom Sheet */}
      <AnimatePresence data-id="element-459" data-mp="1jhxxnk6v0a2q">
        {showFilters && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setShowFilters(false)} className="fixed inset-0 bg-black/40 z-[100]" data-id="element-460" data-mp="1jg3g8fusr03e" />
            <motion.div initial={{
          y: '100%'
        }} animate={{
          y: 0
        }} exit={{
          y: '100%'
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 200
        }} className="fixed bottom-0 left-0 right-0 bg-white rounded-t-[24px] p-6 z-[101] pb-[env(safe-area-inset-bottom)] md:max-w-[480px] md:mx-auto md:rounded-[24px] md:bottom-6 md:left-6 md:right-6" data-id="element-461" data-mp="zt5fd65ii9e4">
              <div className="w-[40px] h-[4px] bg-border rounded-full mx-auto mb-6 md:hidden" data-id="element-462" data-mp="14emq0zdfpu2o" />
              <h2 className="text-[18px] font-semibold text-text-primary mb-6" data-id="element-463" data-mp="19esniw6dyaso">
                Filter Alerts
              </h2>

              <div className="space-y-6 mb-8" data-id="element-464" data-mp="ug71n45skywv">
                <div data-id="element-465" data-mp="zg33oxuxig9s">
                  <label id="status-label" className="block text-[14px] font-medium text-text-secondary mb-3" data-id="element-466" data-mp="zfjvi8v2z68y">
                    Status
                  </label>
                  <div className="flex bg-secondary p-1 rounded-pill" role="group" aria-labelledby="status-label" data-id="element-467" data-mp="3k73nvf4q7hvp">
                    <button onClick={() => setStatusFilter('active')} className={`flex-1 py-2 rounded-pill text-[14px] font-medium transition-colors ${statusFilter === 'active' ? 'bg-white text-text-primary shadow-sm' : 'text-text-secondary hover:text-text-primary'}`} data-id="element-468" data-mp="1lpjrmvqbndu9">
                      Active
                    </button>
                    <button onClick={() => setStatusFilter('resolved')} className={`flex-1 py-2 rounded-pill text-[14px] font-medium transition-colors ${statusFilter === 'resolved' ? 'bg-white text-text-primary shadow-sm' : 'text-text-secondary hover:text-text-primary'}`} data-id="element-469" data-mp="12wxw6cjbtpa7">
                      Resolved
                    </button>
                  </div>
                </div>

                <div data-id="element-470" data-mp="2nbrksj3fuxre">
                  <label id="severity-label" className="block text-[14px] font-medium text-text-secondary mb-3" data-id="element-471" data-mp="2uxmxfzrsypsv">
                    Severity
                  </label>
                  <div className="flex flex-wrap gap-2" role="group" aria-labelledby="severity-label" data-id="element-472" data-mp="34o7tpgk07i8m">
                    {['all', 'high', 'medium', 'low'].map(sev => {
                  const isActive = severityFilters.includes(sev);
                  return <button key={sev} onClick={() => toggleSeverityFilter(sev)} className={`px-4 py-2 rounded-pill text-[14px] font-medium transition-colors capitalize border ${isActive ? 'bg-primary text-white border-primary' : 'bg-white text-text-secondary border-border hover:border-primary/50'}`} data-id="element-473" data-mp="2gzf4e2xw08mz">
                          {sev}
                        </button>;
                })}
                  </div>
                </div>
              </div>

              <Button onClick={() => setShowFilters(false)} className="w-full h-[52px] mb-4" data-id="element-474" data-mp="3vwrisns9a1vs">
                Apply Filters
              </Button>
              <button onClick={() => {
            setStatusFilter('active');
            setSeverityFilters(['all']);
          }} className="w-full py-2 text-primary font-medium text-[15px]" data-id="element-475" data-mp="8onu7aaofg9u">
                Clear Filters
              </button>
            </motion.div>
          </>}
      </AnimatePresence>

      {/* Resolve All Modal */}
      <AnimatePresence data-id="element-476" data-mp="rijkm9t89wwb">
        {showResolveAllModal && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => !isResolvingAll && setShowResolveAllModal(false)} className="fixed inset-0 bg-black/40 backdrop-blur-[4px] z-[100] flex items-center justify-center p-4" data-id="element-477" data-mp="16e8ytls695qh">
              <motion.div initial={{
            scale: 0.92,
            opacity: 0
          }} animate={{
            scale: 1,
            opacity: 1
          }} exit={{
            scale: 0.92,
            opacity: 0
          }} transition={{
            duration: 0.25,
            ease: 'easeOut'
          }} onClick={e => e.stopPropagation()} className="bg-white rounded-[24px] p-7 w-full max-w-[320px] text-center shadow-elevated" data-id="element-478" data-mp="1g1cxf7np10hi">
                <AlertTriangle size={40} className="text-danger mx-auto mb-4" data-id="element-479" data-mp="nbmhct2i3oir" />
                <h2 className="text-[20px] font-semibold text-text-primary mb-2" data-id="element-480" data-mp="48kp6ma57omm">
                  Resolve all alerts?
                </h2>
                <p className="text-[15px] text-text-secondary mb-8" data-id="element-481" data-mp="3vl5n150gh553">
                  This will mark all {activeAlertsCount} active alerts as
                  resolved. This cannot be undone.
                </p>

                <div className="space-y-3" data-id="element-482" data-mp="3lmg1n29cp2k3">
                  <button onClick={handleResolveAll} disabled={isResolvingAll} className="w-full h-[52px] bg-danger text-white rounded-[14px] font-medium text-[16px] flex items-center justify-center transition-opacity disabled:opacity-70" data-id="element-483" data-mp="n7gh1qyj3qxa">
                    {isResolvingAll ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-484" data-mp="8c0jhbyqpcw5" /> : 'Yes, Resolve All'}
                  </button>
                  <button onClick={() => setShowResolveAllModal(false)} disabled={isResolvingAll} className="w-full h-[52px] bg-transparent text-text-secondary rounded-[14px] font-medium text-[16px]" data-id="element-485" data-mp="18icp1qog9a8a">
                    Cancel
                  </button>
                </div>
              </motion.div>
            </motion.div>
          </>}
      </AnimatePresence>
    </main>;
}
function AlertCard({
  alert,
  onResolve
}: {
  alert: Alert;
  onResolve?: () => void;
}) {
  const navigate = useNavigate();
  const [isSwiping, setIsSwiping] = useState(false);
  const [translateX, setTranslateX] = useState(0);
  const [isResolved, setIsResolved] = useState(false);
  const startXRef = useRef<number | null>(null);
  const currentXRef = useRef<number | null>(null);
  const formatType = (type: string) => {
    return type.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };
  const getRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} min ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hr ago`;
    const isYesterday = new Date(now.setDate(now.getDate() - 1)).toDateString() === date.toDateString();
    if (isYesterday) {
      return `Yesterday at ${date.toLocaleTimeString([], {
        hour: 'numeric',
        minute: '2-digit'
      })}`;
    }
    return date.toLocaleDateString([], {
      month: 'short',
      day: 'numeric'
    });
  };
  const getSeverityStyles = (severity: string) => {
    switch (severity) {
      case 'high':
      case 'critical':
        return {
          border: 'border-l-danger',
          iconBg: 'bg-danger/15',
          iconColor: 'text-danger',
          Icon: AlertCircle
        };
      case 'medium':
        return {
          border: 'border-l-[#F59E0B]',
          iconBg: 'bg-[#F59E0B]/15',
          iconColor: 'text-[#F59E0B]',
          Icon: AlertTriangle
        };
      case 'low':
      default:
        return {
          border: 'border-l-success',
          iconBg: 'bg-success/15',
          iconColor: 'text-success',
          Icon: Info
        };
    }
  };
  const styles = getSeverityStyles(alert.severity);
  const Icon = styles.Icon;
  const handleTouchStart = (e: React.TouchEvent) => {
    if (alert.status !== 'active') return;
    startXRef.current = e.touches[0].clientX;
    setIsSwiping(true);
  };
  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isSwiping || startXRef.current === null) return;
    currentXRef.current = e.touches[0].clientX;
    const diff = currentXRef.current - startXRef.current;
    // Only allow swiping left
    if (diff < 0) {
      // Add resistance after 100px
      const boundedDiff = diff < -100 ? -100 + (diff + 100) * 0.2 : diff;
      setTranslateX(boundedDiff);
    }
  };
  const handleTouchEnd = () => {
    if (!isSwiping) return;
    setIsSwiping(false);
    if (translateX < -80) {
      // Stay open
      setTranslateX(-80);
    } else {
      // Snap back
      setTranslateX(0);
    }
    startXRef.current = null;
    currentXRef.current = null;
  };
  const handleResolveClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onResolve) {
      setIsResolved(true);
      setTimeout(() => {
        onResolve();
      }, 300); // Wait for animation
    }
  };
  return <motion.div layout initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: isResolved ? 0 : 1,
    x: isResolved ? -100 : 0,
    height: isResolved ? 0 : 'auto',
    marginBottom: isResolved ? 0 : 16
  }} exit={{
    opacity: 0,
    height: 0,
    marginBottom: 0
  }} transition={{
    duration: 0.3
  }} className="relative overflow-hidden rounded-[20px]" data-id="element-486" data-mp="1rwfg4a0kqv1t">
      {/* Background Resolve Button (revealed on swipe) */}
      {alert.status === 'active' && <div className="absolute inset-y-0 right-0 w-[80px] bg-success flex items-center justify-center rounded-r-[20px]" data-id="element-487" data-mp="1pazcj8pgloci">
          <button onClick={handleResolveClick} className="w-full h-full flex flex-col items-center justify-center text-white" data-id="element-488" data-mp="1kj7ksuukhcks">
            <Check size={24} data-id="element-489" data-mp="1ua2fmnlvpkhu" />
            <span className="text-[11px] font-medium mt-1" data-id="element-490" data-mp="1u90ufqb5fhme">Resolve</span>
          </button>
        </div>}

      {/* Main Card */}
      <div className={`bg-white rounded-[20px] shadow-card border-l-4 ${styles.border} w-full relative z-10 transition-transform lg:hover:-translate-y-[2px] lg:hover:shadow-elevated cursor-pointer`} style={{
      transform: `translateX(${translateX}px)`,
      transition: isSwiping ? 'none' : 'transform 0.3s cubic-bezier(0.2, 0.8, 0.2, 1)'
    }} onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd} onClick={() => {
      if (translateX < -10) {
        setTranslateX(0);
        return;
      }
      navigate(`/app/alerts/${alert.id}`);
    }} data-id="element-491" data-mp="287h9q5n2ugg5">
        <div className="p-4" data-id="element-492" data-mp="2mw413tckkkvn">
          <div className="flex items-start gap-3 mb-4" data-id="element-493" data-mp="2ry3595g4vltn">
            <div className={`w-[40px] h-[40px] rounded-full flex items-center justify-center flex-shrink-0 ${styles.iconBg}`} data-id="element-494" data-mp="2rvgz9a8w5edj">
              <Icon size={20} className={styles.iconColor} data-id="element-495" data-mp="31n4kgteutz6k" />
            </div>

            <div className="flex-1 min-w-0 pt-1" data-id="element-496" data-mp="35oq4cypt3dq9">
              <h3 className="text-[15px] font-semibold text-text-primary mb-1" data-id="element-497" data-mp="35og9oxttgie8">
                {formatType(alert.type)}
              </h3>
              <p className="text-[13px] text-text-secondary line-clamp-2 leading-relaxed" data-id="element-498" data-mp="3fmw5he3sgfu2">
                {alert.message}
              </p>
            </div>

            <ChevronRight size={20} className="text-text-secondary mt-1 flex-shrink-0" data-id="element-499" data-mp="3psdw0audghf2" />
          </div>

          <div className="h-[1px] w-full bg-border mb-3" data-id="element-500" data-mp="3fsmxzz6lrka8" />

          <div className="flex items-center justify-between" data-id="element-501" data-mp="3ft6j1orjuosh">
            <span className="text-[12px] text-text-secondary font-medium" data-id="element-502" data-mp="3bscvwxlxle0n">
              {getRelativeTime(alert.triggeredAt)}
            </span>

            {alert.status === 'active' ? <div className="px-3 py-1 bg-danger/10 text-danger text-[12px] font-semibold rounded-pill" data-id="element-503" data-mp="7cti9mk3icu8">
                Active
              </div> : <div className="px-3 py-1 bg-success/10 text-success text-[12px] font-semibold rounded-pill" data-id="element-504" data-mp="3uq5n52n6zzi7">
                Resolved
              </div>}
          </div>
        </div>
      </div>
    </motion.div>;
}